import React, { useEffect, useState } from 'react';
import { HiChartBar, HiRefresh, HiServer, HiUserGroup } from 'react-icons/hi';
import { analyticsService } from '../services/api';
import DistributionCard from '../components/dashboard/DistributionCard';
import SyncActivityChart from '../components/dashboard/SyncActivityChart';
import { HeroFigure, Meter, StatTile } from '../components/dashboard/StatTiles';
import {
  DayActivity,
  FailedDocuments,
  RecentSyncs,
  SiteBreakdown,
} from '../components/dashboard/OperationsPanels';
import { STATUS, relativeTime } from '../components/dashboard/vizTokens';

const DashboardPage = () => {
  const [summary, setSummary] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sum, act] = await Promise.all([
        analyticsService.getSummary(),
        analyticsService.getActivity(),
      ]);
      setSummary(sum);
      setActivity(act);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch dashboard data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  if (loading && !summary) {
    return (
      <div className="page-container flex items-center justify-center min-h-64">
        <p className="text-slate-400 text-sm">Loading dashboard…</p>
      </div>
    );
  }

  const health = activity?.pipeline_health;
  const docsToday = activity?.today?.documents_added ?? 0;
  const docsYesterday = activity?.yesterday?.documents_added ?? 0;

  return (
    // While refetching, hold the previous render at reduced opacity rather than
    // flashing a skeleton — no layout jump, numbers never disappear mid-read.
    <div className={`page-container transition-opacity ${loading ? 'opacity-60' : 'opacity-100'}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Dashboard</h2>
          <p className="text-sm text-slate-400 mt-0.5">Document pipeline and sync health</p>
        </div>
        <button
          onClick={fetchAll}
          disabled={loading}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50"
        >
          <HiRefresh className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
          {lastRefresh
            ? `Updated ${relativeTime(lastRefresh.toISOString().replace('Z', ''))}`
            : 'Refresh'}
        </button>
      </div>

      {/* Lead: one hero figure, the success meter, and the supporting counters */}
      {summary && (
        <div className="card mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,14rem)_minmax(0,18rem)_1fr] gap-8 items-center">
            <HeroFigure
              label="Total documents"
              value={summary.total_documents}
              caption={
                docsToday > 0
                  ? `${docsToday.toLocaleString()} added today`
                  : 'No new documents today'
              }
            />

            {health && <Meter label="OCR success rate" rate={health.success_rate} />}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatTile
                label="Processing"
                value={summary.by_status?.processing ?? 0}
                tone={STATUS.warning}
                /* A queue depth alone cannot be read — the same number means
                   "working through it" or "stalled". The rate says which. */
                hint={
                  health?.completed_last_hour
                    ? `${health.completed_last_hour.toLocaleString()}/h${
                        health.backlog_eta_hours ? ` · ~${health.backlog_eta_hours}h left` : ''
                      }`
                    : (summary.by_status?.processing ?? 0) > 0
                    ? 'Queue not moving'
                    : 'In the OCR queue'
                }
              />
              <StatTile
                label="Completed"
                value={summary.by_status?.completed ?? 0}
                tone={STATUS.good}
                hint="Searchable"
              />
              <StatTile
                label="Failed"
                value={summary.by_status?.failed ?? 0}
                tone={STATUS.critical}
                hint={(summary.by_status?.failed ?? 0) > 0 ? 'Needs attention' : 'None'}
              />
              <StatTile
                label="Unindexed"
                value={health?.unindexed ?? 0}
                tone={STATUS.serious}
                hint={(health?.unindexed ?? 0) > 0 ? 'Not yet searchable' : 'All indexed'}
              />
            </div>
          </div>
        </div>
      )}

      {activity && (
        <>
          {/* items-start stops the short chart card from being stretched to the
              height of a long failure list beside it. */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6 items-start">
            <div className="lg:col-span-2">
              <SyncActivityChart weekly={activity.weekly} />
            </div>
            <DayActivity
              title="Today"
              data={{ ...activity.today, documents_added: docsToday }}
              errorSyncs={activity.error_syncs}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <RecentSyncs syncs={activity.recent_syncs} />
            <SiteBreakdown bySite={activity.by_site} />
          </div>
        </>
      )}

      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <DistributionCard title="Document types" icon={HiChartBar} data={summary.by_type} />
          <DistributionCard title="Sources" icon={HiServer} data={summary.by_source} sortByCount />
          <DistributionCard
            title="Departments"
            icon={HiUserGroup}
            data={summary.by_department}
            sortByCount
            emptyHint="No department assigned yet"
          />
        </div>
      )}

      {activity?.failed_documents?.length > 0 && (
        <FailedDocuments docs={activity.failed_documents} />
      )}

      {activity && docsYesterday > 0 && (
        <p className="text-xs text-slate-400 mt-4">
          Yesterday: {docsYesterday.toLocaleString()} documents added.
        </p>
      )}
    </div>
  );
};

export default DashboardPage;
